import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId } from "../lib/scroll";
import { useLang, LANGS } from "../i18n";

const LINKS = ["about", "houses", "experiences", "gallery", "booking"];

const LangSwitch = ({ light }) => {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="lang-switch" className={`flex items-center rounded-full border p-0.5 ${light ? "border-[#F7F5F0]/40" : "border-[#1C1D1A]/20"}`}>
      {LANGS.map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          className={`font-mono text-[11px] uppercase tracking-wider w-9 h-8 rounded-full transition-colors ${
            lang === l ? "bg-[#B85D3B] text-[#F7F5F0]" : light ? "text-[#F7F5F0] hover:bg-[#F7F5F0]/15" : "text-[#1C1D1A] hover:bg-[#1C1D1A]/10"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t } = useLang();
  useEffect(() => {
    const onScroll = () => { setScrolled(window.scrollY > 40); };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => { setOpen(false); scrollToId(id); };
  const light = !scrolled && !open;

  return (
    <motion.header
      data-testid="nav-container"
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 border-b ${
        scrolled || open ? "bg-[#F7F5F0]/90 backdrop-blur-xl border-[#E2DDD5] py-3" : "bg-transparent border-transparent py-6"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 flex items-center justify-between">
        <button data-testid="nav-logo" onClick={() => go("top")} aria-label="Início">
          <Logo light={light} />
        </button>
        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map((id) => (
            <button
              key={id}
              data-testid={`nav-link-${id === "booking" ? "contact" : id}`}
              onClick={() => go(id)}
              className={`nav-link text-sm tracking-wide ${light ? "text-[#F7F5F0]" : "text-[#1C1D1A]"}`}
            >
              {t.nav[id]}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <LangSwitch light={light} />
          <button
            data-testid="nav-cta-booking"
            onClick={() => go("booking")}
            className="max-md:!hidden btn-pill bg-[#B85D3B] text-[#F7F5F0] hover:bg-[#9E4C2E]"
          >
            {t.nav.cta} <ArrowUpRight className="w-4 h-4" />
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className={`lg:hidden p-2 ${light ? "text-[#F7F5F0]" : "text-[#1C1D1A]"}`}
            aria-label="Menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-drawer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden px-5"
          >
            <div className="flex flex-col py-6 gap-1">
              {LINKS.map((id, i) => (
                <motion.button
                  key={id}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.05 * i }}
                  data-testid={`nav-mobile-link-${id}`}
                  onClick={() => go(id)}
                  className="text-left font-display text-4xl py-2 text-[#1C1D1A]"
                >
                  {t.nav[id]}
                </motion.button>
              ))}
              <button
                data-testid="nav-mobile-cta"
                onClick={() => go("booking")}
                className="btn-pill bg-[#B85D3B] text-[#F7F5F0] mt-4 self-start"
              >
                {t.nav.cta} <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
